import Button from '@atlaskit/button/default/button';

import Heading from '@atlaskit/heading/heading';

import { Box } from '@atlaskit/primitives/box';

import { Inline } from '@atlaskit/primitives/inline';

import { Stack } from '@atlaskit/primitives/stack';

import { xcss } from '@atlaskit/primitives/xcss/xcss';

import { Hide } from '@atlaskit/primitives/compiled/hide';

import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics/cross-flow/hooks';

// eslint-disable-next-line @atlaskit/platform/no-barrel-entry-imports -- Remote placements must use the host-shared Crossflow context.
import { Journeys, useCrossFlow } from '@atlassiansox/cross-flow-support';

import { Targets } from '@atlassiansox/cross-flow-component-support/cross-flow-base-types';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import CelebrateDark from '../assets/CelebrateDark';

import CelebrateLight from '../assets/CelebrateLight';

import { messages } from '../messages';

export const CarouselCompleteState = (): JSX.Element | null => {
	const [isCarouselOpen, setIsCarouselOpen] = useState(true);
	const { colorMode } = useThemeObserver();

	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { fireViewedUIEvent, fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();
	const crossFlow = useCrossFlow();

	useEffect(() => {
		fireViewedUIEvent(
			{
				action: 'viewed',
				actionSubject: 'section',
				actionSubjectId: 'twcoCarousel',
			},
			{
				source: 'twcoCarousel',
				recommendedProductIds: [],
			},
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleDiscoverMoreClick = async () => {
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'discoverMoreApps',
			},
			{
				recommendedProductIds: [],
				targetProductId: Targets.TEAMWORK_COLLECTION,
				source: 'twcoCarouselButton',
			},
		);

		if (crossFlow.isEnabled) {
			await crossFlow.api.open({
				sourceComponent: 'twcoCarousel',
				sourceContext: 'discoverMoreApps',
				journey: Journeys.DISCOVER,
			});
		}
	};

	const handleCloseCarousel = () => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'carouselCloseButton',
			actionSubjectId: 'townsquareOnboardingCarouselCloseButton',
			attributes: { cards: [] },
		});
		setIsCarouselOpen(false);
	};

	if (!isCarouselOpen) {
		return null;
	}

	return (
		<Stack space="space.150" testId="townsquare-onboarding-widget-complete-state">
			<Heading size="small">
				<FormattedMessage {...messages.carouselHeading} />
			</Heading>
			<Box
				backgroundColor="elevation.surface.sunken"
				paddingBlock="space.150"
				paddingInline="space.200"
				xcss={xcss({ borderRadius: 'radius.small' })}
			>
				<Inline spread="space-between" alignBlock="center" shouldWrap>
					<Inline space="space.100" alignBlock="center">
						<Hide below="xs">{colorMode === 'light' ? <CelebrateLight /> : <CelebrateDark />}</Hide>
						<FormattedMessage {...messages.carouselCompleteMessage} />
					</Inline>
					<Inline space="space.100">
						{/* eslint-disable-next-line @post-office/message-linting/require-instrumented-interactions -- Placement completion UI has no message instance; these handlers emit Crossflow/GASv3 events. */}
						<Button onClick={handleDiscoverMoreClick}>
							<FormattedMessage {...messages.completeCtaButtonLabel} />
						</Button>
						{/* eslint-disable-next-line @post-office/message-linting/require-instrumented-interactions -- Placement completion UI has no message instance; these handlers emit Crossflow/GASv3 events. */}
						<Button appearance="subtle" onClick={handleCloseCarousel}>
							<FormattedMessage {...messages.dismissButtonLabel} />
						</Button>
					</Inline>
				</Inline>
			</Box>
		</Stack>
	);
};
